import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { HelpOutline } from '@mui/icons-material';
import { showMessages as intercomShowMessagingComponent } from '@intercom/messenger-js-sdk';

import { MouseEvent, useState } from 'react';

import { Logo } from '@farmshare/ui-components';
import { useCognitoAuth } from '../../auth/cognito-auth-context';
import { ProducerNavModule } from './producer-nav-config';
import { ProfileMenu } from './profile-menu';

interface NavItemProps {
  highlighted: boolean;
  connected: boolean;
}

const NavItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'connected',
})<NavItemProps>(({ theme, highlighted, connected }) => ({
  width: 84,
  height: 84,
  padding: theme.spacing(1),
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: highlighted
    ? theme.palette.background.default
    : 'transparent',
  borderRadius: 8,
  position: 'relative',
  ...(highlighted &&
    connected && {
      zIndex: 1201,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: theme.spacing(3),
        transform: 'translateX(100%)',
        backgroundColor: theme.palette.background.default,
      },
    }),
}));

interface IconRailProps {
  modules: ProducerNavModule[];
  activeModule: string | null;
  hoveredModule: string | null;
  isSecNavVisible: boolean;
  onHoverModule: (moduleId: string | null) => void;
  onClickModule: (mod: ProducerNavModule) => void;
  userName?: string;
  onLogout: () => void;
}

export function IconRail({
  modules,
  activeModule,
  hoveredModule,
  isSecNavVisible,
  onHoverModule,
  onClickModule,
  userName,
  onLogout,
}: IconRailProps) {
  const { impersonateUserId } = useCognitoAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isActive = (id: string) => id === activeModule;
  const isHovered = (id: string) => id === hoveredModule;

  const handleAvatarClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const initial = userName?.charAt(0).toUpperCase() || '?';

  return (
    <Stack
      sx={{
        width: 100,
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        p: 2,
        '@media print': { display: 'none' },
      }}
      onMouseLeave={() => {
        onHoverModule(null);
      }}
    >
      <Logo
        sx={{
          width: 40,
          height: 40,
          color: 'background.default',
          display: 'block',
        }}
      />
      {/* Nav modules */}
      <Stack
        spacing={4}
        sx={{ flex: 1, width: '100%', alignItems: 'center', pt: 1, mt: 5 }}
      >
        {modules.map((mod) => {
          const active = isActive(mod.id);
          const hovered = isHovered(mod.id);
          const highlighted = hovered || (active && !hoveredModule);

          return (
            <NavItem
              key={mod.id}
              direction="column"
              highlighted={highlighted}
              connected={highlighted && isSecNavVisible}
              onMouseEnter={() => onHoverModule(mod.id)}
              onClick={() => onClickModule(mod)}
            >
              <Box
                sx={{
                  color: highlighted ? 'primary.main' : 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': { fontSize: 36 },
                }}
              >
                {mod.icon}
              </Box>
              <Typography
                className="nav-label"
                variant="body2"
                sx={{
                  fontSize: '0.86rem',
                  fontWeight: highlighted ? 600 : 400,
                  color: highlighted ? 'primary.main' : 'background.default',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  mt: 0.5,
                }}
              >
                {mod.label}
              </Typography>
            </NavItem>
          );
        })}
      </Stack>

      {/* Chat with us */}
      <IconButton
        onClick={() => intercomShowMessagingComponent()}
        sx={{
          color: 'background.default',
          mb: 2,
          '&:hover, &:active': {
            bgcolor: 'background.default',
            color: 'primary.main',
          },
        }}
      >
        <HelpOutline sx={{ fontSize: 32 }} />
      </IconButton>

      {/* User avatar */}
      <Avatar
        onClick={handleAvatarClick}
        sx={{
          width: 44,
          height: 44,
          bgcolor: 'background.default',
          color: 'primary.main',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          mb: 1,
          ...(impersonateUserId && {
            border: '2px solid',
            borderColor: 'error.main',
          }),
        }}
      >
        {initial}
      </Avatar>

      <ProfileMenu
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        onLogout={onLogout}
      />
    </Stack>
  );
}
